import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Save, Trash2, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { zavriNaPozadi } from "@/lib/modalDirtyGuard";
import { zalozSmlouvu, NOVE_TYPY_POJISTEK, POZNAMKA_Z_ANALYZY } from "@/lib/analyzaV2/propsaniSmluv";
import { textChybySmlouvy } from "@/lib/chybaSmlouvy";
import { useTextySmluv, prikladyCastek, textCisla } from "@/lib/smlouvy/textySmluv";
import { INSTITUCE } from "@/lib/instituce";

/* Seznam druhů = backend models_contracts.ContractType (hlídá
 * __tests__/druhySmluvSlozka.test.js — druh mimo seznam by backend odmítl 422). */
export const CONTRACT_TYPES = [
  { k: "hypoteka", label: "Hypotéka" },
  { k: "stavebni", label: "Stavební spoření" },
  { k: "uver", label: "Úvěr" },
  { k: "investice", label: "Investice" },
  { k: "penzijko", label: "Důchod (DPS/DIP)" },
  { k: "pojisteni_zivot", label: "Životní pojištění" },
  { k: "pojisteni_auta", label: "Pojištění auta" },
  { k: "pojisteni_majetek", label: "Pojištění majetku" },
  { k: "pojisteni_odpovednost", label: "Pojištění odpovědnosti" },
  { k: "pojisteni_ostatni", label: "Ostatní pojištění (právní ochrana, cestovní…)" },
  { k: "zdravotni", label: "Zdravotní" },
  { k: "jine", label: "Jiné" },
];

const COMMON_PROVIDERS = [
  "Allianz", "NN", "Generali", "Kooperativa", "ČSOB", "Česká pojišťovna",
  "UNIQA", "AXA", "Maxima", "MetLife", "Slavia", "KB", "Komerční banka",
  "Hypoteční banka", "Moneta", "Air Bank", "Raiffeisenbank", "mBank",
  "Fio", "Equa", "OberBank", "Wüstenrot", "ČMSS", "Modrá pyramida",
  "Conseq", "ČSOB Penzijní", "Generali ČS", "NN Penzijní",
];

/* Našeptávač institucí podle ZEMĚ TENANTA (ověření 4. 10. 2026): slovenský
 * poradce dřív dostával Českou pojišťovnu, ČMSS, Modrou pyramidu, KB…
 * SK = slovenské instituce z centrálního katalogu (lib/instituce/katalog.js,
 * country „SK“ — nic vymyšleného) + skupiny, které na Slovensku působí pod
 * stejnou značkou. Český seznam beze změny. Poradce může napsat cokoli —
 * je to jen nápověda, ne výběr. */
const ZNACKY_I_NA_SLOVENSKU = ["Kooperativa", "UNIQA", "MetLife", "mBank"];
export function navrhyInstituci(zeme) {
  if (zeme !== "SK") return COMMON_PROVIDERS;
  const sk = INSTITUCE.filter((i) => i.country === "SK" && i.active).map((i) => i.displayName);
  return [...new Set([...sk, ...ZNACKY_I_NA_SLOVENSKU])];
}

/* Popisek s měnou: Row má CSS uppercase a z „(Kč)“ dělal „(KČ)“. Symbol se
 * proto vysází ve vlastním <span class="normal-case">; text pro čtečku
 * zůstává stejný („Měsíční částka (Kč)“). */
const ZNACKA_MENY = "\u0001";
function popisekSMenou(text, symbol) {
  const casti = String(text).split(ZNACKA_MENY);
  if (casti.length < 2) return text;
  return (
    <>
      {casti[0]}
      <span className="normal-case" data-testid="popisek-mena">{symbol}</span>
      {casti.slice(1).join(symbol)}
    </>
  );
}

const HAS_FIXATION = new Set(["hypoteka", "stavebni", "uver"]);
export const HAS_ANNIVERSARY = new Set([
  "pojisteni_auta", "pojisteni_majetek", "pojisteni_zivot", "zdravotni",
  "pojisteni_odpovednost", "pojisteni_ostatni",
]);

/* Průběžný stav smlouvy — kolik zbývá / kolik je naspořeno / co to má dneska
 * za hodnotu. Detail smlouvy (/smlouvy/:id) tahle čísla ukazuje jako to hlavní
 * nahoře. Do 9. 8. 2026 je ale nešlo nikde zadat (model je neznal) a stránka je
 * nahrazovala konstantami ze seed skriptu. Buď se pole dá vyplnit, nebo se
 * nesmí zobrazovat — třetí možnost není. */
const HAS_REMAINING = new Set(["hypoteka", "uver"]);
const HAS_SAVED = new Set(["penzijko", "stavebni"]);
const HAS_CURRENT_VALUE = new Set(["investice"]);

/**
 * ContractEditor — full Add/Edit modal for client contracts.
 * - Create mode: contract=null, contactId required
 * - Edit mode: contract={id, ...}
 * Calls POST /api/contracts or PATCH /api/contracts/{id}.
 * Calls onSaved(contract) after success so parent can refresh.
 */
/* JAZYK × MĚNA (4. 10. 2026, majitel „ano“): všechny texty editoru přes t()
 * v pěti jazycích, měna u částek ze ZEMĚ TENANTA (CZ → Kč, SK → €) — dřív
 * natvrdo „(Kč)“ i u slovenského poradce. Políčka: písmo 16 px (iOS jinak
 * při psaní přiblíží stránku), výška 44 px, u částek desetinná klávesnice. */
const POLE = "w-full min-h-[44px] rounded-md px-2.5 py-2 text-[16px] outline-none";
const POLE_STYL = { background: "var(--surface)", border: "1px solid var(--line)", color: "var(--ink)" };

export default function ContractEditor({ open, contract, contactId, onClose, onSaved, onDeleted }) {
  const isEdit = !!contract?.id;
  const [form, setForm] = useState(() => emptyForm());
  const { lang, t, locale, mena, zeme } = useTextySmluv();
  const sMenou = (klic) => popisekSMenou(t(klic, { mena: ZNACKA_MENY }), mena.symbol);
  const priklad = prikladyCastek(mena.code);
  const napr = (n) => t("smlouvaEditor.napr", { x: textCisla(n, locale) });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  /* Výchozí podoba formuláře při otevření. Editace i „Nová smlouva" z analýzy
   * startují PŘEDVYPLNĚNÉ, takže „je něco vyplněno?" by hlásilo dirty pořád.
   * Dirty = liší se od tohohle výchozího stavu. */
  const [vychozi, setVychozi] = useState(() => emptyForm());

  useEffect(() => {
    if (open) {
      const start = isEdit ? hydrate(contract) : (readContractPrefill(contactId) || emptyForm());
      setForm(start);
      setVychozi(start);
    }
  }, [open, contract, isEdit, contactId]);

  if (!open) return null;

  const save = async () => {
    if (!form.type) {
      toast.error(t("smlouvaEditor.typChybi"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type: form.type,
        provider: (form.provider || "").trim(),
        contract_number: (form.contract_number || "").trim(),
        start_date: form.start_date || null,
        fixation_end: form.fixation_end || null,
        anniversary_date: form.anniversary_date || null,
        monthly_amount: form.monthly_amount === "" ? null : Number(form.monthly_amount),
        total_amount: form.total_amount === "" ? null : Number(form.total_amount),
        // Průběžný stav — nevyplněné jde jako null a backend ho zahodí
        // (exclude_none). Nevyplněné tedy zůstane nevyplněné, nikdy nulou.
        remaining_amount: form.remaining_amount === "" ? null : Number(form.remaining_amount),
        saved_amount: form.saved_amount === "" ? null : Number(form.saved_amount),
        current_value: form.current_value === "" ? null : Number(form.current_value),
        rate: form.rate === "" ? null : Number(form.rate),
        notes: form.notes || "",
        status: form.status || "active",
      };
      /* STARÝ BACKEND (před redeployem na Emergentu) nové typy pojistek
       * (NOVE_TYPY_POJISTEK) nezná a odmítne je 422. Stejně jako propis
       * z analýzy (zalozSmlouvu) se smlouva uloží jednou znovu jako „Jiné“
       * — poradce o nic nepřijde a hláška mu řekne proč. Jiná chyba jde
       * do toastu jako věta (textChybySmlouvy), editor zůstane otevřený. */
      let jakoJine = false;
      const odeslat = (body) => {
        jakoJine = body.type === "jine" && payload.type !== "jine";
        return isEdit ? api.patch(`/api/contracts/${contract.id}`, body) : api.post("/api/contracts", body);
      };
      const { data: saved } = await zalozSmlouvu(odeslat, isEdit ? payload : { contact_id: contactId, ...payload });
      if (jakoJine && NOVE_TYPY_POJISTEK.includes(payload.type)) {
        toast.warning(t("smlouvaEditor.ulozenoJakoJine", "Uloženo jako „Jiné“ — tento druh smlouvy server zatím nezná."));
      } else {
        toast.success(isEdit ? t("smlouvaEditor.ulozeno") : t("smlouvaEditor.pridano"));
      }
      onSaved?.(saved);
      onClose?.();
    } catch (e) {
      toast.error(textChybySmlouvy(e, t("smlouvaEditor.ukladaniSelhalo"), lang));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm(t("smlouvaEditor.smazatPotvrzeni"))) return;
    setDeleting(true);
    try {
      await api.delete(`/api/contracts/${contract.id}`);
      toast.success(t("smlouvaEditor.smazano"));
      onDeleted?.(contract.id);
      onClose?.();
    } catch (e) {
      toast.error(textChybySmlouvy(e, t("smlouvaEditor.mazaniSelhalo"), lang));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/60 backdrop-blur-sm"
        // 12 vyplněných polí se nesmí ztratit omylem kliknutým pixelem vedle okna.
        onClick={zavriNaPozadi({
          zmeneno: JSON.stringify(form) !== JSON.stringify(vychozi),
          onClose,
          lang,
        })}
        data-testid="contract-editor-overlay"
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
          transition={{ type: "spring", damping: 28, stiffness: 320 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl overflow-hidden"
          style={{
            /* Tokeny motivu místo krémového #FDFBF7 natvrdo — v tmavém
               motivu (Midnight) bylo světlé --ink na krémové ploše nečitelné. */
            background: "var(--bg)",
            border: "1px solid var(--line)",
            color: "var(--ink)",
            boxShadow: "0 30px 80px rgba(0,0,0,0.30)",
          }}
          data-testid="contract-editor-modal"
        >
          <div className="flex items-center justify-between px-5 py-3.5" style={{ borderBottom: "1px solid var(--line)" }}>
            <div className="flex items-center gap-2">
              <FileText size={16} style={{ color: "var(--accent)" }} />
              <h3 className="font-display text-[16px] font-semibold" style={{ color: "var(--ink)" }}>
                {isEdit ? t("smlouvaEditor.nadpisEditace") : t("smlouvaEditor.nadpisNova")}
              </h3>
            </div>
            <button onClick={onClose} className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-black/5" data-testid="contract-editor-close" aria-label={t("smlouvaEditor.zavrit")}>
              <X size={16} style={{ color: "var(--muted)" }} />
            </button>
          </div>

          <div className="px-5 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
            <Row label={t("smlouvaEditor.typSmlouvy")} htmlFor="contract-type">
              <select
                id="contract-type"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                data-testid="contract-type"
                className={POLE}
                style={POLE_STYL}
              >
                <option value="">{t("smlouvaEditor.vyber")}</option>
                {CONTRACT_TYPES.map((typ) => (
                  <option key={typ.k} value={typ.k}>{t(`smlouvaEditor.typy.${typ.k}`, typ.label)}</option>
                ))}
              </select>
            </Row>

            <div className="grid grid-cols-2 gap-3">
              <Row label={t("smlouvaEditor.instituce")} htmlFor="contract-provider">
                <input
                  id="contract-provider"
                  list="provider-suggestions"
                  value={form.provider}
                  onChange={(e) => setForm({ ...form, provider: e.target.value })}
                  data-testid="contract-provider"
                  placeholder={t("smlouvaEditor.napr", { x: "Uniqa" })}
                  className={POLE}
                  style={POLE_STYL}
                />
                <datalist id="provider-suggestions">
                  {navrhyInstituci(zeme).map((p) => <option key={p} value={p} />)}
                </datalist>
              </Row>
              <Row label={t("smlouvaEditor.cislo")} htmlFor="contract-number">
                <input
                  id="contract-number"
                  value={form.contract_number}
                  onChange={(e) => setForm({ ...form, contract_number: e.target.value })}
                  data-testid="contract-number"
                  placeholder={t("smlouvaEditor.napr", { x: "4567890123" })}
                  className={POLE}
                  style={POLE_STYL}
                />
              </Row>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Row label={sMenou("smlouvaEditor.mesicni")} htmlFor="contract-monthly">
                <input
                  id="contract-monthly"
                  type="number" min="0" step="any" inputMode="decimal"
                  value={form.monthly_amount}
                  onChange={(e) => setForm({ ...form, monthly_amount: e.target.value })}
                  data-testid="contract-monthly"
                  placeholder={napr(priklad.mesicni)}
                  className={`${POLE} tabular-nums`}
                  style={POLE_STYL}
                />
              </Row>
              <Row label={sMenou("smlouvaEditor.celkova")} htmlFor="contract-total">
                <input
                  id="contract-total"
                  type="number" min="0" step="any" inputMode="decimal"
                  value={form.total_amount}
                  onChange={(e) => setForm({ ...form, total_amount: e.target.value })}
                  data-testid="contract-total"
                  placeholder={napr(priklad.celkova)}
                  className={`${POLE} tabular-nums`}
                  style={POLE_STYL}
                />
              </Row>
            </div>

            {/* Průběžný stav smlouvy — jen u typů, kde dává smysl. Bez něj
                detail smlouvy nemá z čeho spočítat splácení / průběh spoření
                a řekne to nahlas místo aby si číslo domyslel. */}
            {(HAS_REMAINING.has(form.type) || HAS_SAVED.has(form.type) || HAS_CURRENT_VALUE.has(form.type)) && (
              <div className="grid grid-cols-2 gap-3">
                {HAS_REMAINING.has(form.type) && (
                  <Row label={sMenou("smlouvaEditor.zbyva")} htmlFor="contract-remaining">
                    <input
                      id="contract-remaining"
                      type="number" min="0" step="any" inputMode="decimal"
                      value={form.remaining_amount}
                      onChange={(e) => setForm({ ...form, remaining_amount: e.target.value })}
                      data-testid="contract-remaining"
                      placeholder={napr(priklad.zbyva)}
                      className={`${POLE} tabular-nums`}
                      style={POLE_STYL}
                    />
                  </Row>
                )}
                {HAS_SAVED.has(form.type) && (
                  <Row label={sMenou("smlouvaEditor.nasporeno")} htmlFor="contract-saved">
                    <input
                      id="contract-saved"
                      type="number" min="0" step="any" inputMode="decimal"
                      value={form.saved_amount}
                      onChange={(e) => setForm({ ...form, saved_amount: e.target.value })}
                      data-testid="contract-saved"
                      placeholder={napr(priklad.nasporeno)}
                      className={`${POLE} tabular-nums`}
                      style={POLE_STYL}
                    />
                  </Row>
                )}
                {HAS_CURRENT_VALUE.has(form.type) && (
                  <Row label={sMenou("smlouvaEditor.hodnota")} htmlFor="contract-current-value">
                    <input
                      id="contract-current-value"
                      type="number" min="0" step="any" inputMode="decimal"
                      value={form.current_value}
                      onChange={(e) => setForm({ ...form, current_value: e.target.value })}
                      data-testid="contract-current-value"
                      placeholder={napr(priklad.hodnota)}
                      className={`${POLE} tabular-nums`}
                      style={POLE_STYL}
                    />
                  </Row>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Row label={t("smlouvaEditor.sazba")} htmlFor="contract-rate">
                <input
                  id="contract-rate"
                  type="number" min="0" max="100" step="0.01" inputMode="decimal"
                  value={form.rate}
                  onChange={(e) => setForm({ ...form, rate: e.target.value })}
                  data-testid="contract-rate"
                  placeholder={t("smlouvaEditor.napr", { x: textCisla(4.89, locale) })}
                  className={`${POLE} tabular-nums`}
                  style={POLE_STYL}
                />
              </Row>
              <Row label={t("smlouvaEditor.zacatek")} htmlFor="contract-start">
                <input
                  id="contract-start"
                  type="date"
                  value={form.start_date || ""}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  data-testid="contract-start"
                  className={POLE}
                  style={POLE_STYL}
                />
              </Row>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {HAS_FIXATION.has(form.type) && (
                <Row label={t("smlouvaEditor.fixace")} htmlFor="contract-fixation">
                  <input
                    id="contract-fixation"
                    type="date"
                    value={form.fixation_end || ""}
                    onChange={(e) => setForm({ ...form, fixation_end: e.target.value })}
                    data-testid="contract-fixation"
                    className={POLE}
                    style={POLE_STYL}
                  />
                </Row>
              )}
              {HAS_ANNIVERSARY.has(form.type) && (
                <Row label={t("smlouvaEditor.vyroci")} htmlFor="contract-anniversary">
                  <input
                    id="contract-anniversary"
                    type="date"
                    value={form.anniversary_date || ""}
                    onChange={(e) => setForm({ ...form, anniversary_date: e.target.value })}
                    data-testid="contract-anniversary"
                    className={POLE}
                    style={POLE_STYL}
                  />
                </Row>
              )}
            </div>

            <Row label={t("smlouvaEditor.stav")}>
              <div className="flex gap-1.5 flex-wrap" role="group" aria-label={t("smlouvaEditor.stav")}>
                {[
                  { k: "active", label: t("smlouvaEditor.stavActive") },
                  { k: "expired", label: t("smlouvaEditor.stavExpired") },
                  { k: "cancelled", label: t("smlouvaEditor.stavCancelled") },
                ].map((s) => (
                  <button
                    key={s.k}
                    type="button"
                    onClick={() => setForm({ ...form, status: s.k })}
                    data-testid={`contract-status-${s.k}`}
                    aria-pressed={form.status === s.k}
                    className="min-h-[44px] px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
                    style={form.status === s.k
                      ? { background: "var(--accent-soft)", color: "var(--accent)", border: "1px solid var(--accent)" }
                      : { background: "var(--surface)", color: "var(--muted)", border: "1px solid var(--line)" }
                    }
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </Row>

            <Row label={t("smlouvaEditor.poznamky")} htmlFor="contract-notes">
              <textarea
                id="contract-notes"
                rows={2}
                /* Datový marker smlouvy z analýzy (POZNAMKA_Z_ANALYZY, česky — slouží
                   i jako dedup pro starý backend) se ZOBRAZÍ v jazyce poradce, ale
                   uloží se beze změny; přepíše-li ho poradce, uloží se jeho text
                   (velká kontrola 5. 10. 2026, T1-16). */
                value={form.notes === POZNAMKA_Z_ANALYZY ? t("smlouvaKarta.poznamkaZAnalyzy", POZNAMKA_Z_ANALYZY) : form.notes}
                onChange={(e) => {
                  const v = e.target.value;
                  const zpet = form.notes === POZNAMKA_Z_ANALYZY && v === t("smlouvaKarta.poznamkaZAnalyzy", POZNAMKA_Z_ANALYZY);
                  setForm({ ...form, notes: zpet ? POZNAMKA_Z_ANALYZY : v });
                }}
                data-testid="contract-notes"
                placeholder={t("smlouvaEditor.poznamkyNapr")}
                className={`${POLE} resize-none`}
                style={POLE_STYL}
              />
            </Row>
          </div>

          <div className="flex items-center justify-between gap-3 px-5 py-3" style={{ borderTop: "1px solid var(--line)", background: "var(--surface-2)" }}>
            {isEdit ? (
              <button
                onClick={remove}
                disabled={deleting || saving}
                data-testid="contract-delete"
                className="flex items-center gap-1.5 min-h-[44px] text-xs px-3 py-2 rounded-lg transition-colors hover:bg-red-500/10 disabled:opacity-50"
                style={{ color: "var(--negative-text)" }}
              >
                {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                {t("smlouvaEditor.smazat")}
              </button>
            ) : <span />}
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="min-h-[44px] px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{ background: "var(--surface)", border: "1px solid var(--line)", color: "var(--ink)" }}
                data-testid="contract-cancel"
              >
                {t("smlouvaEditor.zrusit")}
              </button>
              <button
                onClick={save}
                disabled={saving}
                data-testid="contract-save"
                className="flex items-center gap-1.5 min-h-[44px] px-5 py-2 rounded-lg text-sm font-semibold transition-all hover:translate-y-[-1px] disabled:opacity-50"
                style={{
                  background: "var(--accent)",
                  color: "var(--on-accent)",
                  boxShadow: "0 4px 14px var(--accent-soft), inset 0 1px 0 rgba(255,255,255,0.16)",
                }}
              >
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                {isEdit ? t("smlouvaEditor.ulozitZmeny") : t("smlouvaEditor.pridat")}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Row({ label, htmlFor, children }) {
  /* Popisek svázaný s polem (htmlFor) — čtečka u políčka přečte „Měsíční
     částka (€)“, ne jen „upravit text“. */
  const Popisek = htmlFor ? "label" : "div";
  return (
    <div>
      <Popisek htmlFor={htmlFor} className="block text-popisek mb-1 uppercase tracking-wider font-medium" style={{ color: "var(--muted)" }}>{label}</Popisek>
      {children}
    </div>
  );
}

/* Most Návrh → Smlouva: přečte předvyplnění uložené z návrhu řešení (ať poradce
   nepíše produkty 2×). Spotřebuje (smaže) jen když sedí klient. */
function readContractPrefill(contactId) {
  try {
    const raw = localStorage.getItem("beplan_contract_prefill");
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (p.contact_id && contactId && p.contact_id !== contactId) return null;
    localStorage.removeItem("beplan_contract_prefill");
    return {
      ...emptyForm(),
      type: p.type || "investice",
      provider: p.provider || "",
      monthly_amount: p.monthly_amount != null ? String(p.monthly_amount) : "",
      total_amount: p.total_amount != null ? String(p.total_amount) : "",
      notes: p.notes || "",
    };
  } catch { return null; }
}

function emptyForm() {
  return {
    type: "",
    provider: "",
    contract_number: "",
    monthly_amount: "",
    total_amount: "",
    remaining_amount: "",
    saved_amount: "",
    current_value: "",
    rate: "",
    start_date: "",
    fixation_end: "",
    anniversary_date: "",
    status: "active",
    notes: "",
  };
}

function hydrate(c) {
  return {
    type: c.type || "",
    provider: c.provider || "",
    contract_number: c.contract_number || "",
    monthly_amount: c.monthly_amount ?? "",
    total_amount: c.total_amount ?? "",
    remaining_amount: c.remaining_amount ?? "",
    saved_amount: c.saved_amount ?? "",
    current_value: c.current_value ?? "",
    rate: c.rate ?? "",
    start_date: c.start_date || "",
    fixation_end: c.fixation_end || "",
    anniversary_date: c.anniversary_date || "",
    status: c.status || "active",
    notes: c.notes || "",
  };
}
